import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Volume2 } from "lucide-react";
import api from "@/lib/api";
import { playBigCheers } from "@/lib/sfx";
import { playSay, playSayHighlight, stopReading } from "@/lib/readingAudio";
import KikoCheer from "@/components/KikoCheer";
import StoryRecorder from "@/components/reading/StoryRecorder";
import { Pic } from "@/components/reading/ReadingExercise";
import { ReadingBg, Btn } from "@/pages/ReadingHome";

// Kiko story book: the child reads each page; tap a word to hear it, or let Kiko read the page.
export default function ReadingStory() {
  const { childId, storyId } = useParams();
  const navigate = useNavigate();
  const [s, setS] = useState(null);
  const [pg, setPg] = useState(0);
  const [hi, setHi] = useState(-1);
  const [kiko, setKiko] = useState(null);
  const readRef = useRef(0);
  useEffect(() => {
    api.get(`/reading/${childId}/story/${storyId}`).then(({ data }) => setS(data)).catch(() => navigate(`/kid/${childId}/reading`));
    return () => stopReading();
  }, [childId, storyId, navigate]);
  if (!s) return <ReadingBg petit><div className="p-10 text-center font-bold">…</div></ReadingBg>;
  const fr = s.lang === "fr";
  const end = pg >= s.pages.length;
  const page = s.pages[pg];
  const words = page ? page.text.split(" ") : [];
  const readPage = async () => {
    const my = ++readRef.current;
    await playSayHighlight(page.text, s.lang, (k) => my === readRef.current && setHi(k));
  };
  const go = (n) => {
    readRef.current += 1;
    stopReading(); setHi(-1); setPg(n);
    if (n >= s.pages.length) {
      playBigCheers();
      const msg = fr ? "Bravo ! Tu as lu toute l'histoire !" : "Well done! You read the whole story!";
      setKiko({ n: Date.now(), msg, kind: "win", streak: 10 });
      playSay(msg, s.lang);
    }
  };
  return (
    <ReadingBg petit>
      <header className="max-w-4xl mx-auto px-5 pt-5 flex items-center gap-3">
        <button onClick={() => navigate(`/kid/${childId}/reading/${s.lang}`)} className="w-11 h-11 rounded-full bg-white border-[3px] border-slate-900 flex items-center justify-center" data-testid="story-back"><ArrowLeft /></button>
        <h1 className="text-2xl sm:text-3xl font-bold flex-1" data-testid="story-title">📚 {s.title}</h1>
        <span className="font-bold" data-testid="story-page-count">{Math.min(pg + 1, s.pages.length)}/{s.pages.length}</span>
      </header>
      <main className="max-w-4xl mx-auto px-5 py-6 flex flex-col items-center gap-6">
        <AnimatePresence mode="wait">
          {!end ? (
            <motion.div key={pg} initial={{ rotateY: 80, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }} exit={{ rotateY: -80, opacity: 0 }} transition={{ duration: 0.4 }}
              className="w-full rounded-[2rem] bg-white border-[3px] border-slate-900 shadow-[8px_8px_0_0_#0F172A] p-5 sm:p-8 grid md:grid-cols-2 gap-6 items-center" data-testid="story-page">
              <Pic k={page.pic} label="📖" className="w-full aspect-square max-h-80" />
              <div className="flex flex-wrap gap-x-3 gap-y-2 justify-center text-4xl sm:text-5xl font-bold leading-tight" data-testid="story-text">
                {words.map((w, k) => (
                  <motion.button key={k} onClick={() => playSay(w.replace(/[.!?,]/g, ""), s.lang)} data-testid={`story-word-${k}`}
                    animate={{ scale: hi === k ? 1.15 : 1, color: hi === k ? "#2563EB" : "#0F172A" }} transition={{ duration: 0.1 }}
                    className={`rounded-xl px-1 ${hi === k ? "bg-yellow-200" : "hover:bg-yellow-100"}`} data-active={hi === k}>
                    {w}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div key="end" initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center gap-4 text-center" data-testid="story-end">
              <motion.img src="/mascot/kiko.png" alt="Kiko" className="w-40 h-40" animate={{ y: [0, -18, 0], rotate: [0, 8, -8, 0] }} transition={{ repeat: Infinity, duration: 1.3 }} />
              <h2 className="text-3xl sm:text-4xl font-bold">{fr ? "Tu as lu toute l'histoire !" : "You read the whole story!"}</h2>
              <div className="flex gap-3 flex-wrap justify-center">
                <Btn onClick={() => go(0)} testid="story-again" color="bg-yellow-300">{fr ? "Relire" : "Read again"}</Btn>
                <Btn onClick={() => navigate(`/kid/${childId}/reading/${s.lang}`)} testid="story-to-path" color="bg-green-500 text-white">{fr ? "Mon parcours" : "My path"}</Btn>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        {!end && (
          <div className="flex gap-3 flex-wrap justify-center">
            {pg > 0 && <Btn onClick={() => go(pg - 1)} testid="story-prev" color="bg-white"><ArrowLeft size={18} /></Btn>}
            <Btn onClick={readPage} testid="story-read" color="bg-yellow-300"><Volume2 size={18} /> {fr ? "Kiko lit" : "Kiko reads"}</Btn>
            <Btn onClick={() => go(pg + 1)} testid="story-next" color="bg-blue-600 text-white">{fr ? "Page suivante" : "Next page"} <ArrowRight size={18} /></Btn>
          </div>
        )}
        {!end && <StoryRecorder childId={childId} storyId={storyId} page={pg} fr={fr} />}
      </main>
      <KikoCheer cheer={kiko} />
    </ReadingBg>
  );
}
